import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "415",
  "slug": "what-is-the-dymaxion-map",
  "title": "What Is the Dymaxion Map? The Projection That Unfolds the Globe",
  "excerpt": "Buckminster Fuller's Dymaxion map folds the Earth onto a 20-sided shape and unwraps it flat, showing the continents with almost no distortion and no fixed up.",
  "date": "2026-06-12",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1521295121783-8a321d551ad2?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx3b3JsZCUyMGdsb2JlJTIwbWFwfGVufDF8MHx8fDE3ODEyNTIzMDJ8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "3 min",
  "author": "EarthGuessr Team",
  "tldr": "The Dymaxion map, designed by Buckminster Fuller in the 1940s, projects the globe onto an unfolded icosahedron, a 20-sided shape. It shows the continents with very little distortion of size or shape, but at the cost of any fixed sense of north and a continuous ocean.",
  "content": [
    {
      "type": "paragraph",
      "text": "Every flat map of a round planet lies somewhere. The familiar Mercator map keeps directions and shapes honest but blows up the size of the far north and south. Most projections force you to choose what to sacrifice. The Dymaxion map, designed by the American inventor and architect Buckminster Fuller, tries to cheat the trade-off with an unusual trick: instead of stretching the globe onto a flat sheet, it folds it onto a solid and then unwraps it."
    },
    {
      "type": "heading",
      "text": "Who made it"
    },
    {
      "type": "paragraph",
      "text": "Buckminster Fuller, the same restless thinker behind the geodesic dome, introduced his map in the 1940s, with a famous version printed in Life magazine in 1943 and a refined edition in 1954. He gave it the name Dymaxion, a word he coined from dynamic, maximum, and tension, and applied to several of his inventions, including a car and a prefabricated house."
    },
    {
      "type": "heading",
      "text": "How it works"
    },
    {
      "type": "paragraph",
      "text": "The 1954 version projects the surface of the Earth onto an icosahedron, a shape with twenty triangular faces. Imagine wrapping the globe in a faceted shell, transferring the coastlines onto each triangle, and then carefully unfolding that shell flat. Because each triangle covers only a small slice of the globe, the distortion within any one face stays small. The result is a map where the sizes and shapes of the continents are kept much closer to the truth than on most familiar projections."
    },
    {
      "type": "heading",
      "text": "What it gets right"
    },
    {
      "type": "paragraph",
      "text": "The Dymaxion map's great strength is that it shows the landmasses with very little distortion of area or shape, and without privileging any one country by placing it in the centre or at the top. Fuller liked to unfold it so that the continents form one almost-connected chain, what he called one island in one ocean, a deliberate argument that humanity shares a single, interconnected world."
    },
    {
      "type": "heading",
      "text": "What it sacrifices"
    },
    {
      "type": "paragraph",
      "text": "All of that comes at a price. The Dymaxion map has no consistent up, so there is no simple north on the page, and you cannot read a compass bearing off it the way you can with Mercator. The oceans are sliced apart at the folds, which makes it useless for plotting a sea voyage and unsettling if you are used to seeing the seas as continuous. It is a map for thinking about the whole Earth, not for finding your way across it."
    },
    {
      "type": "heading",
      "text": "How it compares to other projections"
    },
    {
      "type": "paragraph",
      "text": "Every projection makes a different bargain. Mercator preserves direction and local shape, which is why it ruled navigation for centuries, but it badly exaggerates land near the poles. Equal-area maps like the Gall-Peters keep sizes honest but stretch shapes. Compromise maps like the Winkel Tripel spread the errors around so none of them is glaring. The Dymaxion belongs to a rarer family that accepts a broken, rotatable layout in exchange for keeping both size and shape unusually faithful."
    },
    {
      "type": "heading",
      "text": "Why it still matters"
    },
    {
      "type": "paragraph",
      "text": "The Dymaxion map endures because it makes a point no ordinary map can. By refusing to put any nation on top or in the middle, and by showing the land at honest sizes, it quietly challenges the mental picture most of us carry from a lifetime of looking at one or two standard projections. It is a reminder that a map is always an argument about how to see the world, not a neutral photograph of it."
    }
  ]
};
